import Button from 'components/Basic/Button';
import { checkPanelDrawerHasUnfinalizedFacets, deleteAllFacetsFromPanelDrawer } from 'components/tool/utils';
import { useRefs } from 'contexts/RefContext';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from 'store';
import { setDrawModeState, setDrawnFacetExists, toggleGroundMount } from 'store/slices/ToolSlice';
import useQueriesRunningInBackGround from 'hooks/useQueriesRunningInBackGround';
import useScreenDims from 'hooks/useScreenDims';
import { selectIsAnyToolActionActive, TOOL_ACTION } from 'store/selectors/toolSelector';
import { useEffect } from 'react';
import useCreateDeleteFacet from 'hooks/api/facet/useCreateDeleteFacet';
import { changePanel } from 'store/slices/RoofDataSlice/roofDataSlice';
import { toggleManualToolModalState } from 'store/slices/ManualToolModal';
import { FACET_TYPE } from '../../../../../../constants';
import { clearToolModeHandlers, registerToolModeHandlers } from 'components/tool/Editor/ToolRegistry';
import { isModeDefault } from '../../../../../../util';
import { selectIsRecomputingDsm } from 'store/slices/DsmShadingSlice/selectors';

export default function DrawBtn() {
	const { drawModeEnabled, roofIndexes, newlyCreatedFacets, groundMountEnabled } = useSelector((state: RootState) => state.toolNewPostions.data);
	const { uuid, panel, designType, mode } = useSelector((state: RootState) => state.roofData.data);
	const defaultToGroundMountFacet = useSelector((state: RootState) => (
		state.roofData.data.missingRoofPolygon ||
		(designType === 'SOLAR' && state.roofData.data.org.setting.defaultDrawnFacetType === FACET_TYPE.FLAT)
	));

	const queriesRunningInBackground = useQueriesRunningInBackGround();
	const isAnyToolActionActive = useSelector(selectIsAnyToolActionActive);
	const isRecomputing = useSelector(selectIsRecomputingDsm);
	const disableWhenOtherAction = (!drawModeEnabled && !!isAnyToolActionActive);
	const shouldDisable = queriesRunningInBackground || roofIndexes.length > 0 || disableWhenOtherAction || isRecomputing;

	const { konvaRef } = useRefs();
	const dispatch = useDispatch<AppDispatch>();
	const screenDims = useScreenDims();
	const { width: screenWidth } = screenDims;
	const isMobileView = screenWidth < 860;
	const createDeleteFacetMutation = useCreateDeleteFacet();

	function handleDrawFacetClick() {
		if (drawModeEnabled && konvaRef?.current && checkPanelDrawerHasUnfinalizedFacets(konvaRef.current)) {
			deleteAllFacetsFromPanelDrawer(konvaRef.current);
		}
		dispatch(setDrawModeState({ enabled: !drawModeEnabled }));
		dispatch(toggleGroundMount({ groundMountEnabled: !!defaultToGroundMountFacet }));
	}

	useEffect(() => {
		const cancelHandler = () => {
			if (!konvaRef?.current) return;
			deleteAllFacetsFromPanelDrawer(konvaRef.current);
			dispatch(setDrawModeState({ enabled: false }));
			dispatch(toggleGroundMount({ groundMountEnabled: false }));
			dispatch(setDrawnFacetExists({ exists: false }));
		};

		const doneHandler = async () => {
			if (!newlyCreatedFacets || Object.keys(newlyCreatedFacets).length === 0) {
				dispatch(setDrawModeState({ enabled: false }));
				dispatch(toggleGroundMount({ groundMountEnabled: false }));
				return;
			}
			const defaultMode = isModeDefault(mode);
			const hasFacetCreatedInNonDefaultModes = !defaultMode;
			const isNewlyAddedFacetsGroundMountInDefaultMode = defaultMode && groundMountEnabled;
			if (isNewlyAddedFacetsGroundMountInDefaultMode || hasFacetCreatedInNonDefaultModes) {
				dispatch(toggleManualToolModalState({ value: true }));
				return;
			}
			await createDeleteFacetMutation.mutateAsync();
			await dispatch(changePanel({ uuid, panelKey: panel.key, designType })).unwrap();

			dispatch(setDrawModeState({ enabled: false }));
			dispatch(toggleGroundMount({ groundMountEnabled: false }));
		};

		registerToolModeHandlers(TOOL_ACTION.DRAW_FACET, {
			cancel: cancelHandler,
			done: doneHandler,
			cancelConfirmMessage: 'Are you sure you want to discard the newly drawn facets?',
		});

		return () => {
			clearToolModeHandlers(TOOL_ACTION.DRAW_FACET);
		};
	}, [dispatch, konvaRef, createDeleteFacetMutation, newlyCreatedFacets, uuid, panel.key, designType, mode, groundMountEnabled]);

	return (
		<>
			<Button 
				id="draw-facet"
				style={{
					background: `${drawModeEnabled ? 'var(--toolActiveBgColor)' : 'var(--defaultBgColor)'}`,
					color: `${drawModeEnabled ? 'var(--primary)' : 'var(--labelValueColor)'}`,
				}}
				className={`${drawModeEnabled ? 'active' : ''} btn-tools btn-drawfacet`}
				disabled={shouldDisable}
				onClick={handleDrawFacetClick}
				tooltipContent={drawModeEnabled ? '' : 'Draw Facet'}>
				<svg 
					width={isMobileView ? '23' : '25'}
					height={isMobileView ? '17' : '18'}
					viewBox="0 0 25 18" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d="M19.6153 10.3085L23.6043 16.9568H1L6.92018 7.64917H10.3077" stroke={`${drawModeEnabled ? 'var(--primary)' : 'var(--defaultIconColor)'}`} strokeWidth="1.69284" strokeLinecap="round" strokeLinejoin="round"/>
					<path d="M19.742 2.29927L21.0413 1L23.1308 3.08948L21.8807 4.33956M19.742 2.29927L12.6834 9.35791L11.6387 12.4921L14.7729 11.4474L21.8807 4.33956M19.742 2.29927L21.8807 4.33956" stroke={`${drawModeEnabled ? 'var(--primary)' : 'var(--defaultIconColor)'}`} strokeWidth="1.69284" strokeLinecap="round" strokeLinejoin="round"/>
				</svg>
			</Button>
		</>
	);

}